import * as DG from "ui/dialogs"
import {
  ChangeEvent,
  Fragment,
  useCallback,
  useState,
  MouseEvent as ReactMouseEvent,
} from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import {
  FormulaPart,
  MediaConfigScope,
  MediaField,
  ValidationType,
} from "../../GlobalMediaTypes/types"
import {
  fetchMediaFields,
  patchField,
  validateField,
} from "api/globalMediaTypes"
import { useMutation, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { ReactComponent as CheckIcon } from "assets/check.svg"
import { FormulaRow } from "./FormulaRow"
import { ReactComponent as InfoIcon } from "assets/icon-infocolorless.svg"
import { Market } from "models/regions"
import { PartOperatoreMenu } from "./PartOperatorMenu"
import { css } from "@emotion/css"
import { globalState } from "../../GlobalMediaFields/List"
import set from "lodash/fp/set"
import { unset } from "lodash"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"

export function operatorOptions(t: (key: string) => string) {
  return [
    { label: "", value: "" },
    { label: t("admin.mediaFields.calculated.operation.+"), value: "+" },
    { label: t("admin.mediaFields.calculated.operation./"), value: "/" },
    { label: t("admin.mediaFields.calculated.operation.*"), value: "*" },
    { label: t("admin.mediaFields.calculated.operation.-"), value: "-" },
  ]
}

const initialState = (data: FormulaPart[]) =>
  data?.length
    ? data
    : [
        {
          leftOperand: { fixedValue: undefined },
          rightOperand: { fixedValue: undefined },
        },
      ]

export type CalculatedFormulaProps = {
  open: boolean
  onClose: () => void
  data: FormulaPart[]
  row: MediaField | Market
  configScope?: MediaConfigScope
  configOwnerId?: string
}

export function CalculatedFormulaModal(props: CalculatedFormulaProps) {
  const {
    open,
    onClose,
    data,
    row,
    configScope = "GLOBAL",
    configOwnerId,
  } = props
  const { t } = useI18n()

  const [formula, setFormula] = useState<FormulaPart[]>(() =>
    initialState(data),
  )
  const [errors, setErrors] = useState<ErrorsMap | undefined>()
  const [isValidated, setIsValidated] = useState<boolean>(false)
  const [validationMessage, setValidationMessage] = useState<string>()
  const { hasAuthority } = useAuthenticated()

  // ** fetch fields
  const { data: mediaFields } = useQuery(
    [
      "global-media-numeric-fields",
      { configScope, configOwnerId, operands: true, includeCalculated: true },
    ],
    fetchMediaFields,
  )

  // this should be fixed in the back end but causes more troubles there
  const operands = mediaFields?.filter((f) => f.semanticCode !== "budget")

  // ** validate field
  const [validate] = useMutation(validateField, {
    onSuccess: (d: ValidationType) => {
      setIsValidated(d.valid)
      setValidationMessage(t("admin.mediaFields.validation.success"))
      setErrors(undefined)
    },
    onError: (e: ApiError) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
      setValidationMessage(t("admin.mediaFields.validation.error"))
    },
  })

  // ** patch field
  const [patch] = useMutation(patchField, {
    onSuccess: (d: MediaField) => {
      globalState.updateRow(d)
      setErrors(undefined)
      setIsValidated(false)
      setValidationMessage("")
      onClose()
    },
    onError: (e: ApiError) => {
      const error = e as ApiError
      const errorMap = error?.errorsMap ?? {}
      setErrors(errorMap)
    },
  })

  const handlePartOperandChange = (
    name: string,
    idx: string,
    value?: string,
  ) => {
    const _v = value === "" ? undefined : value

    setFormula((f) => set(`${idx}.${name}`, _v, f))
    setIsValidated(false)
    setValidationMessage("")
    setErrors((e) => ({
      ...e,
      [`formulaSet.formulaParts[${idx}].${name}`]: "",
    }))
    addRow()
  }

  const handleAttrChange = (
    evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined,
  ) => {
    const { name, value } = evt?.target ?? {}
    !!name && setFormula((f) => set(name, { [value as string]: undefined }, f))
    !!name && setErrors(undefined)
    setIsValidated(false)
    setValidationMessage("")
  }

  const handleChange =
    (idx: number) =>
    (evt: ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | undefined) => {
      const { name, value } = evt?.target ?? {}

      !!name && setFormula((f) => set(`${idx}.${name}`, value, f))
      !!name &&
        setErrors((e) => ({
          ...e,
          [`formulaSet.formulaParts[${idx}].${name}`]: "",
        }))
      setIsValidated(false)
      setValidationMessage("")
    }

  const handleClose = () => {
    setFormula(() => initialState(data))
    setErrors(undefined)
    setIsValidated(false)
    setValidationMessage("")
    onClose()
  }

  const addRow = () => {
    setFormula((f) => [
      ...f,
      {
        leftOperand: { fixedValue: undefined },
        rightOperand: { fixedValue: undefined },
      },
    ])
    setIsValidated(false)
    setValidationMessage("")
  }

  const deleteRow = (
    _: ReactMouseEvent<HTMLButtonElement> | undefined,
    index: number,
  ) => {
    const rowHasError = Object.keys(errors ?? {}).some((key) =>
      key.includes(`formulaSet.formulaParts[${index}]`),
    )
    if (rowHasError) {
      const _errors = Object.keys(errors ?? {}).reduce((acc, key) => {
        if (key.includes(`formulaSet.formulaParts[${index}]`)) {
          return acc
        }
        return { ...acc, [key]: errors?.[key] }
      }, {})
      if (Object.keys(_errors).length === 0) {
        setErrors(undefined)
        setValidationMessage("")
      } else {
        setErrors(_errors)
      }
    }

    setFormula((f) => {
      let _options = [...f]
      _options.splice(index, 1)
      if (!!_options.length) {
        unset(_options[_options.length - 1], "partOperator")
      }
      return !!_options.length ? _options : [{}]
    })
  }

  const onUpdate = useCallback(
    async (row, obj) => {
      patch({ id: row.id, ...obj })
    },
    [patch],
  )

  const onSubmit = () => {
    const _formula = formula?.filter(
      (f) => !!f?.leftOperand || !!f?.rightOperand,
    )
    !!_formula?.length &&
      onUpdate(row, { formulaSet: { formulaParts: _formula } })
  }

  const handleValidate = useCallback(
    async (row, obj) => {
      validate({ id: row.id, ...obj })
    },
    [validate],
  )

  const onValidate = () => {
    const _formula = formula?.filter(
      (f) => !!f?.leftOperand || !!f?.rightOperand,
    )
    !!_formula?.length &&
      handleValidate(row, { formulaSet: { formulaParts: _formula } })
  }

  const id = "calculated-formula-dialog"

  const disabled = !hasAuthority(["AUTH_EDIT_MEDIA_FIELD"])

  return (
    <DG.Dialog
      open={open}
      onClose={handleClose}
      id={id}
      classes={{ paper: styles.dialog }}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id} className="BodyBody-1BlackBold">
          {t("calculation.formula")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>
      <DG.DialogContent center paddingTop className={styles.container}>
        {validationMessage ? (
          <div
            className={`${styles.infoContainer} ${
              isValidated ? styles.sucessMessage : styles.errorMessage
            }`}
          >
            {isValidated ? <CheckIcon /> : <InfoIcon />}
            <span>{validationMessage}</span>
          </div>
        ) : null}

        {formula?.map((f, idx) => (
          <Fragment key={idx}>
            {(!!f?.leftOperand || f?.rightOperand) && (
              <FormulaRow
                disabled={disabled}
                deleteRow={deleteRow}
                disableDelete={formula.length === 1}
                idx={idx}
                formula={f}
                handleChange={handleChange(idx)}
                handleAttrChange={handleAttrChange}
                mediaFields={operands?.filter((field) => field.id !== row.id)}
                errors={errors}
              />
            )}
            <PartOperatoreMenu
              disabled={disabled}
              handlePartOperandChange={handlePartOperandChange}
              partOperator={f?.partOperator}
              errors={errors}
              idx={String(idx)}
            />
          </Fragment>
        ))}
      </DG.DialogContent>
      {hasAuthority(["AUTH_EDIT_MEDIA_FIELD"]) ? (
        <DG.DialogFooter>
          <DG.DialogActions>
            <Button variant="outlined" color="primary" onClick={handleClose}>
              {t("modal.label.cancel")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              onClick={() => (isValidated ? onSubmit() : onValidate())}
            >
              {t(
                isValidated
                  ? "admin.mediaFields.calculated.apply"
                  : "admin.mediaFields.calculated.validate",
              )}
            </Button>
          </DG.DialogActions>
        </DG.DialogFooter>
      ) : null}
    </DG.Dialog>
  )
}

const styles = {
  dialog: css`
    max-width: 936px;
  `,
  container: css`
    display: flex;
    flex-direction: column;
    min-width: 467px;
  `,
  textButton: css`
    --btn-padding: 12px;
    --btn-min-width: 0;
    --btn-line-height: 14px;
    --btn-color: inherit;
    --btn-font-weight: normal;
    --btn-bg-hover: transparent;
    --btn-bg-active: transparent;
    --btn-border-color-hover: var(--white);
    --btn-bg-focus-ring-color: transparent;
    width: fit-content;

    &:focus {
      box-shadow: none;
    }
  `,
  infoContainer: css`
    display: flex;
    align-items: center;
    padding: 16px;
    margin-bottom: 24px;
    border-radius: 5px;

    svg {
      margin-right: 10px;
      height: 24px;
      width: 24px;
    }
  `,
  sucessMessage: css`
    border: 1px solid var(--primary-main);
    background-color: var(--primary-light);
    color: var(--primary-main);
  `,

  errorMessage: css`
    border: 1px solid var(--red);
    background-color: var(--lighter-red);
    color: var(--red);
  `,
}
